import React from 'react';
import { CheckCircle, AlertCircle, Clock, FileText, Zap, ArrowRight } from 'lucide-react';

/**
 * RunLogStatement - Renders run log events as inline statements
 *
 * Instead of embedding run logs inside cards, this component renders them
 * as organized, flowing statements that appear before and after cards in the chat.
 *
 * This provides a cleaner, more readable presentation of system activity
 * similar to how Devin shows its work log.
 */

interface RunLogStatementProps {
    content: string;
    timestamp?: Date;
    className?: string;
}

interface ParsedRunLog {
    type: 'step' | 'artifact' | 'verification' | 'status' | 'info';
    label: string;
    detail?: string;
    status?: 'success' | 'error' | 'warning' | 'info' | 'running';
}

function parseRunLogContent(content: string): ParsedRunLog[] {
    const statements: ParsedRunLog[] = [];

    try {
        const data = JSON.parse(content);

        if (data.type === 'run_started') {
            statements.push({
                type: 'status',
                label: 'Run started',
                detail: data.intentName || data.llmPhase,
                status: 'running',
            });
        } else if (data.type === 'step_started') {
            statements.push({
                type: 'step',
                label: data.label || 'Step started',
                status: 'running',
            });
        } else if (data.type === 'step_log') {
            statements.push({
                type: 'info',
                label: data.msg,
                status:
                    data.level === 'error' ? 'error' : data.level === 'warn' ? 'warning' : 'info',
            });
        } else if (data.type === 'artifact_written') {
            statements.push({
                type: 'artifact',
                label: `Artifact: ${data.key}`,
                detail: data.summary,
                status: 'success',
            });
        } else if (data.type === 'verification') {
            statements.push({
                type: 'verification',
                label: `Verify: ${data.verifier}`,
                detail: data.pass ? 'PASS' : 'FAIL',
                status: data.pass ? 'success' : 'error',
            });
        } else if (data.type === 'run_completed') {
            statements.push({
                type: 'status',
                label: data.success ? 'Run completed successfully' : 'Run failed',
                status: data.success ? 'success' : 'error',
            });
        } else if (data.type === 'run_blocked') {
            statements.push({
                type: 'status',
                label: `Blocked: ${data.status}`,
                detail: data.required_actions?.join(', '),
                status: 'warning',
            });
        } else {
            statements.push({
                type: 'info',
                label: content,
                status: 'info',
            });
        }
    } catch {
        statements.push({
            type: 'info',
            label: content,
            status: 'info',
        });
    }

    return statements;
}

function getStatusIcon(status?: string) {
    switch (status) {
        case 'success':
            return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
        case 'error':
            return <AlertCircle className="w-3.5 h-3.5 text-red-400" />;
        case 'warning':
            return <AlertCircle className="w-3.5 h-3.5 text-yellow-400" />;
        case 'running':
            return <Clock className="w-3.5 h-3.5 text-blue-400 animate-pulse" />;
        default:
            return <ArrowRight className="w-3.5 h-3.5 text-slate-400" />;
    }
}

function getTypeIcon(type: string) {
    switch (type) {
        case 'step':
            return <Zap className="w-3.5 h-3.5 text-violet-400" />;
        case 'artifact':
            return <FileText className="w-3.5 h-3.5 text-cyan-400" />;
        case 'verification':
            return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
        default:
            return null;
    }
}

function getStatusColor(status?: string) {
    switch (status) {
        case 'success':
            return 'text-emerald-300';
        case 'error':
            return 'text-red-300';
        case 'warning':
            return 'text-yellow-300';
        case 'running':
            return 'text-blue-300';
        default:
            return 'text-slate-300';
    }
}

export const RunLogStatement: React.FC<RunLogStatementProps> = ({
    content,
    timestamp,
    className = '',
}) => {
    const statements = parseRunLogContent(content);

    if (statements.length === 0) {
        return null;
    }

    return (
        <div className={`py-1 ${className}`}>
            {statements.map((stmt, idx) => (
                <div key={idx} className="flex items-start gap-2 py-1 px-2 text-xs">
                    <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                        {getStatusIcon(stmt.status)}
                        {getTypeIcon(stmt.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className={`${getStatusColor(stmt.status)}`}>{stmt.label}</span>
                        {stmt.detail && (
                            <span className="text-slate-500 ml-1.5">{stmt.detail}</span>
                        )}
                    </div>
                    {timestamp && (
                        <span className="text-slate-600 text-[10px] flex-shrink-0">
                            {timestamp.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
};

export default RunLogStatement;
